import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
} from 'antd';
import PropTypes from 'prop-types';

const BILL_ITEM_TYPES = [
  {
    label: 'Consultation',
    value: 'CONSULTATION',
  },
  {
    label: 'Medicine',
    value: 'MEDICINE',
  },
  {
    label: 'Test',
    value: 'TEST',
  },
  {
    label: 'Procedure',
    value: 'PROCEDURE',
  },
  {
    label: 'Other',
    value: 'OTHER',
  },
];

const PAYMENT_MODES = [
  {
    label: 'Cash',
    value: 'CASH',
  },
  {
    label: 'UPI',
    value: 'UPI',
  },
  {
    label: 'Cheque',
    value: 'CHEQUE',
  },
  {
    label: 'Bank Transfer',
    value: 'BANK_TRANSFER',
  },
];

const EMPTY_BILL_ITEM = {
  itemName: '',
  amount: 0,
  type: undefined,
  modeOfPayment: null,
};

const getPatientId = patient =>
  patient?._id ?? patient?.id ?? null;

const getPatientName = patient =>
  patient?.fullName ||
  patient?.name ||
  patient?.email ||
  'Unknown Patient';

const calculateTotal = items =>
  Array.isArray(items)
    ? items.reduce(
      (total, item) =>
        total + Number(item?.amount || 0),
      0,
    )
    : 0;

const BillModal = ({
  open,
  saving,
  editingBill,
  form,
  patients,
  patientsLoading,
  onCancel,
  onFinish,
}) => {
  const handleValuesChange = (changedValues, allValues) => {
    if ('billItems' in changedValues) {
      form.setFieldsValue({
        totalAmount: calculateTotal(allValues?.billItems),
      });
    }
  };

  const handleFormFinish = values => {
    /*
     * Get modeOfPayment directly from the Ant Design form.
     * This makes sure the selected payment mode is included
     * even if the submitted values object behaves unexpectedly.
     */
    const modeOfPayment =
      form.getFieldValue('modeOfPayment') ||
      values?.modeOfPayment ||
      null;

    const billItems = Array.isArray(values?.billItems)
      ? values.billItems.map(item => ({
        itemName: item?.itemName || '',
        amount: Number(item?.amount || 0),
        type: item?.type || null,

        // Backend format expects this inside each item.
        modeOfPayment: null,
      }))
      : [];

    const billData = {
      patientId: values?.patientId || null,

      // Payment mode at bill level.
      modeOfPayment,

      billItems,

      totalAmount: Number(
        values?.totalAmount || calculateTotal(billItems),
      ),
    };

    console.log('========== BILL PAYLOAD ==========');
    console.log('Selected Mode Of Payment:', modeOfPayment);
    console.log('Complete Bill Payload:', billData);
    console.log('==================================');

    onFinish(billData);
  };

  return (
    <Modal
      title={
        editingBill
          ? 'Edit Patient Bill'
          : 'Create Patient Bill'
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
      forceRender
      width={650}
      mask={{
        closable: !saving,
      }}
      closable={!saving}
    >
      <Form
        form={form}
        preserve={false}
        layout={'vertical'}
        onFinish={handleFormFinish}
        onValuesChange={handleValuesChange}
        className={'mt-4'}
      >
        {/* Patient */}
        <Form.Item
          label={'Patient'}
          name={'patientId'}
          rules={[
            {
              required: true,
              message: 'Please select a patient',
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            loading={patientsLoading}
            placeholder={'Select patient'}
            optionFilterProp={'label'}
            options={patients
              .map(patient => {
                const patientId =
                  getPatientId(patient);

                return patientId
                  ? {
                    value: patientId,
                    label: getPatientName(patient),
                  }
                  : null;
              })
              .filter(Boolean)}
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {/* Mode Of Payment */}
        <Form.Item
          label={'Mode of Payment'}
          name={'modeOfPayment'}
          rules={[
            {
              required: true,
              message: 'Please select payment mode',
            },
          ]}
        >
          <Select
            placeholder={'Select payment mode'}
            options={PAYMENT_MODES}
            allowClear
          />
        </Form.Item>

        {/* Bill Items */}
        <Form.List
          name={'billItems'}
          rules={[
            {
              validator: async (_, items) => {
                if (
                  !Array.isArray(items) ||
                  !items.length
                ) {
                  throw new Error(
                    'Please add at least one bill item.',
                  );
                }
              },
            },
          ]}
        >
          {(fields, {add, remove}, {errors}) => (
            <>
              <div
                className={
                  'mb-3 flex items-center justify-between'
                }
              >
                <span
                  className={
                    'font-medium text-gray-700'
                  }
                >
                  Bill Items
                </span>

                <Button
                  type={'dashed'}
                  onClick={() =>
                    add({
                      ...EMPTY_BILL_ITEM,
                    })
                  }
                >
                  + Add Item
                </Button>
              </div>

              {fields.map((field, index) => (
                <div
                  key={field.key}
                  className={
                    'mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4'
                  }
                >
                  <div
                    className={
                      'mb-3 flex items-center justify-between'
                    }
                  >
                    <span
                      className={
                        'font-semibold text-gray-700'
                      }
                    >
                      Item {index + 1}
                    </span>

                    {fields.length > 1 && (
                      <Button
                        type={'link'}
                        danger
                        onClick={() =>
                          remove(field.name)
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <div
                    className={
                      'grid grid-cols-1 gap-3 md:grid-cols-3'
                    }
                  >
                    {/* Item Name */}
                    <Form.Item
                      label={'Item Name'}
                      name={[
                        field.name,
                        'itemName',
                      ]}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message:
                            'Enter item name',
                        },
                      ]}
                    >
                      <Input
                        placeholder={
                          'e.g. Consultation'
                        }
                      />
                    </Form.Item>

                    {/* Amount */}
                    <Form.Item
                      label={'Amount'}
                      name={[
                        field.name,
                        'amount',
                      ]}
                      rules={[
                        {
                          required: true,
                          message: 'Enter amount',
                        },
                        {
                          type: 'number',
                          min: 0,
                          message:
                            'Amount cannot be negative',
                        },
                      ]}
                    >
                      <InputNumber
                        className={'w-full'}
                        min={0}
                        precision={2}
                        prefix={'₹'}
                        placeholder={'Amount'}
                      />
                    </Form.Item>

                    {/* Type */}
                    <Form.Item
                      label={'Type'}
                      name={[
                        field.name,
                        'type',
                      ]}
                      rules={[
                        {
                          required: true,
                          message: 'Select type',
                        },
                      ]}
                    >
                      <Select
                        placeholder={'Select type'}
                        options={BILL_ITEM_TYPES}
                      />
                    </Form.Item>
                  </div>
                </div>
              ))}

              <Form.ErrorList errors={errors} />
            </>
          )}
        </Form.List>

        {/* Total Amount */}
        <Form.Item
          label={'Total Amount'}
          name={'totalAmount'}
        >
          <InputNumber
            className={'w-full'}
            prefix={'₹'}
            precision={2}
            disabled
          />
        </Form.Item>

        {/* Buttons */}
        <div
          className={
            'flex justify-end gap-3 pt-4'
          }
        >
          <Button
            onClick={onCancel}
            disabled={saving}
            className={'btn-secondary'}
          >
            Cancel
          </Button>

          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={saving}
            className={'btn-primary'}
          >
            {editingBill
              ? 'Update Bill'
              : 'Create Bill'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

BillModal.propTypes = {
  open: PropTypes.bool.isRequired,

  saving: PropTypes.bool.isRequired,

  editingBill: PropTypes.shape({
    billId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    _id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),

  form: PropTypes.shape({
    setFieldsValue: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,

  patients: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      fullName: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,

  patientsLoading: PropTypes.bool.isRequired,

  onCancel: PropTypes.func.isRequired,

  onFinish: PropTypes.func.isRequired,
};

BillModal.defaultProps = {
  editingBill: null,
};

export default BillModal;
